import {
  useLeaveFeedbackMutation,
  useParticipantSearchInline,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { PerformanceReviewReviewerTypeItems } from "@/types/performance-review.types.ts";
import {
  Box,
  Button,
  Modal,
  ModalProps,
  Radio,
  Select,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

interface FormData {
  employee_id: number | null;
  review_id: number | null;
  reviewer_type_id: number | null;
}

export function PerformanceReviewAddFeedbackModal({ ...props }: ModalProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();
  const navigate = useNavigate();

  const schema = yup.object({
    employee_id: yup
      .number()
      .nullable()
      .defined()
      .test("required", t("errors:field_required"), (value) => value != null),
    review_id: yup
      .number()
      .nullable()
      .defined()
      .test("required", t("errors:field_required"), (value) => value != null),
    reviewer_type_id: yup
      .number()
      .nullable()
      .defined()
      .test("required", t("errors:field_required"), (value) => value != null),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const participantSearch = useParticipantSearchInline();
  const leaveFeedbackMutation = useLeaveFeedbackMutation();

  const employeeId = watch("employee_id");
  const reviewId = watch("review_id");
  const reviewerTypeId = watch("reviewer_type_id");

  const selectedParticipantItem = useMemo(() => {
    if (!employeeId) return null;
    return participantSearch.data?.items.find((p) => p.employee.id === employeeId) ?? null;
  }, [employeeId, participantSearch.data]);

  const handleSelectEmployee = (value: number | string | null) => {
    const numValue = typeof value === "number" ? value : null;
    setValue("employee_id", numValue);
    setValue("review_id", null);
    setValue("reviewer_type_id", null);
  };

  const handleSelectReviewerType = (newReviewId: number, newReviewerTypeId: number) => {
    setValue("review_id", newReviewId);
    setValue("reviewer_type_id", newReviewerTypeId);
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!data.employee_id || !data.review_id || !data.reviewer_type_id) return;

    const result = await leaveFeedbackMutation.mutateAsync({
      reviewId: data.review_id,
      employeeId: data.employee_id,
      reviewTypeId: data.reviewer_type_id,
    });

    close(id);

    navigate(`/performance-reviews/reviewers/${result?.reviewer_id}/respond`);
  });

  const getReviewerTypeTitle = (rt: { type: string; title: string | null }) => {
    return (
      PerformanceReviewReviewerTypeItems().find((item) => item.id === rt.type)?.title ??
      rt.title ??
      t("common:no_title")
    );
  };

  return (
    <Modal
      size={"md"}
      {...props}
      withCloser
      title={t("performance_review:additional_feedback")}
      actions={
        <Stack>
          <Button size={"lg"} onClick={onSubmit} isLoading={leaveFeedbackMutation.isPending}>
            {t("performance_review:start_review")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"md"}>
        <Stack>
          <Controller
            control={control}
            name={"employee_id"}
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("performance_review:additional_feedback_choose_colleague")}
                emptyTitle={t("performance_review:additional_feedback_search_colleague")}
                value={value}
                onChange={(val) => {
                  onChange(val);
                  handleSelectEmployee(Number(val));
                }}
                searchable
                onSearchOptions={participantSearch.search}
                searchOptions={participantSearch.options}
                loadOptionsOnOpen
                isLoading={participantSearch.isLoading}
                required
                error={errors.employee_id?.message}
              />
            )}
          />
          <Text className={"text-sm text-secondary py-2"}>
            {t("performance_review:additional_feedback_description")}
          </Text>
        </Stack>

        {selectedParticipantItem && (
          <Controller
            control={control}
            name={"reviewer_type_id"}
            render={() => (
              <Stack gap={"sm"}>
                <Title
                  size={"xs"}
                  header={t("performance_review:additional_feedback_choose_where")}
                  paddingTop
                />
                {selectedParticipantItem.participants.map((participant) => (
                  <Box
                    key={participant.review.id}
                    className={"rounded-xs py-4 px-6 bg-dark/5 border-1 border-brand-dark/5"}
                  >
                    <Title size={"xs"} header={participant.review.title} />
                    <Stack gap={"xs"} className={"mt-2"}>
                      {participant.reviewer_types.map((rt) => (
                        <Radio
                          key={rt.id}
                          title={getReviewerTypeTitle(rt)}
                          checked={reviewId === participant.review.id && reviewerTypeId === rt.id}
                          onChange={() => handleSelectReviewerType(participant.review.id, rt.id)}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
                {errors.reviewer_type_id && (
                  <Text className={"text-sm text-danger"}>
                    {t("performance_review:additional_feedback_choose_review_error")}
                  </Text>
                )}
              </Stack>
            )}
          />
        )}
      </Stack>
    </Modal>
  );
}
